<template>
  <v-container fluid class="dashboard-container">
    <!-- Header Section -->
    <div class="dashboard-header mb-6">
      <v-row align="center" class="mb-4">
        <v-col cols="12" sm="6">
          <div class="header-content">
            <h1 class="dashboard-title">
              <v-icon large color="primary" class="mr-3">mdi-chart-box</v-icon>
              Dashboard รายงานบุคลากร
            </h1>
            <p class="dashboard-subtitle">สำนักอำนวยการ กลุ่มงานจัดการบุคคล</p>
          </div>
        </v-col>
        <v-col cols="12" sm="6" class="text-right">
          <div class="header-stats">
            <v-chip
              color="success"
              text-color="white"
              class="mr-2 mb-2"
              label
            >
              <v-icon left small>mdi-update</v-icon>
              อัพเดทล่าสุด: {{ formatDate(new Date()) }}
            </v-chip>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Stats Cards Section -->
    <v-row no-gutters class="stats-section mb-6">
      <v-col 
        cols="12" 
        sm="6" 
        md="4" 
        lg="3" 
        v-for="(item, index) in showAlldata" 
        :key="item.id" 
        class="pa-2"
      >
        <v-card 
          class="stats-card"
          elevation="8"
          rounded="xl"
          :style="{ animationDelay: `${index * 0.1}s` }"
        >
          <div class="stats-card-content">
            <!-- Icon Section -->
            <div class="stats-icon-wrapper">
              <div class="stats-icon-bg" :style="{ background: getGradientColor(index) }">
                <v-icon size="32" color="white">{{ getIcon(index) }}</v-icon>
              </div>
              <div class="stats-badge">
                <v-icon size="16" color="success">mdi-trending-up</v-icon>
              </div>
            </div>

            <!-- Content Section -->
            <div class="stats-content">
              <div class="stats-value">
                {{ Number(item.countPerson).toLocaleString() }}
              </div>
              <div class="stats-title">{{ item.position_name }}</div>
              <div class="stats-subtitle">
                <v-icon size="14" color="grey">mdi-account-group</v-icon>
                บุคลากรทั้งหมด
              </div>
            </div>

            <!-- Progress Bar -->
            <div class="stats-progress">
              <v-progress-linear
                :value="getPercentage(item.countPerson)"
                :color="getProgressColor(index)"
                height="4"
                rounded
                background-opacity="0.2"
              ></v-progress-linear>
              <span class="progress-text">{{ getPercentage(item.countPerson) }}%</span>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts Section -->
    <v-row class="charts-section">
      <!-- Personnel Distribution Chart -->
      <v-col cols="12" lg="6" class="pa-2">
        <v-card 
          class="chart-card animate-card"
          elevation="12"
          rounded="xl"
        >
          <v-card-title class="chart-header">
            <div class="chart-title-content">
              <v-icon large color="primary" class="mr-3">mdi-chart-donut</v-icon>
              <div>
                <h3 class="chart-title">การกระจายบุคลากร สอศ.</h3>
                <p class="chart-subtitle">แสดงสัดส่วนบุคลากรแต่ละตำแหน่ง</p>
              </div>
            </div>
          </v-card-title>
          
          <v-card-text class="chart-content">
            <div class="chart-wrapper">
              <apexchart
                height="350"
                type="donut"
                :options="enhancedChartOptions"
                :series="series"
              ></apexchart>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Teacher Academic Level Chart -->
      <v-col cols="12" lg="6" class="pa-2">
        <v-card 
          class="chart-card animate-card"
          elevation="12"
          rounded="xl"
        >
          <v-card-title class="chart-header">
            <div class="chart-title-content">
              <v-icon large color="secondary" class="mr-3">mdi-school</v-icon>
              <div>
                <h3 class="chart-title">วิทยฐานะข้าราชการครู</h3>
                <p class="chart-subtitle">จำแนกตามระดับวิทยฐานะ</p>
              </div>
            </div>
          </v-card-title>
          
          <v-card-text class="chart-content">
            <div class="chart-wrapper">
              <apexchart
                height="350"
                type="donut"
                :options="enhancedChartOptions2"
                :series="series2"
              ></apexchart>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Retirement Data Chart -->
      <v-col cols="12" class="pa-2">
        <v-card 
          class="chart-card animate-card"
          elevation="12"
          rounded="xl"
        >
          <v-card-title class="chart-header">
            <div class="chart-title-content">
              <v-icon large color="warning" class="mr-3">mdi-account-clock</v-icon>
              <div>
                <h3 class="chart-title">ข้อมูลผู้เกษียณอายุราชการ</h3>
                <p class="chart-subtitle">สถิติการเกษียณอายุแยกตามตำแหน่ง</p>
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-chip color="warning" text-color="white" small>
              <v-icon left small>mdi-information</v-icon>
              ข้อมูลประจำปี
            </v-chip>
          </v-card-title>
          
          <v-card-text class="chart-content">
            <div class="chart-wrapper">
              <apexchart 
                height="400"
                :options="enhancedChartOptions3" 
                :series="series3"
              ></apexchart>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Loading Overlay -->
    <v-overlay :value="loading" class="loading-overlay">
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
      ></v-progress-circular>
      <div class="loading-text mt-4">กำลังโหลดข้อมูล...</div>
    </v-overlay>
  </v-container>
</template>

<script>
export default {
  name: "HRvecReportPerson",

  data() {
    return {
      loading: false,
      ApiKey: "HRvec2021",
      showAlldata_rang: [],
      showAlldata: [],
      series: [],
      series2: [],
      showAlldata_retire: [],
      series3: [
        {
          name: "จำนวนผู้เกษียณ",
          data: []
        }
      ],
      
      // Enhanced chart options
      enhancedChartOptions: {
        chart: {
          type: "donut",
          fontFamily: 'Roboto, sans-serif',
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800,
          }
        },
        colors: ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe'],
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'รวมทั้งหมด',
                  color: '#373d3f',
                  fontSize: '16px',
                  fontWeight: 600
                }
              }
            }
          }
        },
        legend: {
          position: 'bottom',
          fontSize: '14px',
          fontWeight: 500,
          markers: {
            width: 12,
            height: 12,
            radius: 6
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val.toFixed(1) + "%";
          },
          style: {
            fontSize: '12px',
            fontWeight: 'bold',
            colors: ['#fff']
          }
        },
        responsive: [{
          breakpoint: 480,
          options: {
            legend: {
              position: 'bottom'
            }
          }
        }]
      },

      enhancedChartOptions2: {
        chart: {
          type: "donut",
          fontFamily: 'Roboto, sans-serif',
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800,
          }
        },
        colors: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#dda0dd'],
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'รวมครู',
                  color: '#373d3f',
                  fontSize: '16px',
                  fontWeight: 600
                }
              }
            }
          }
        },
        legend: {
          position: 'bottom',
          fontSize: '14px',
          fontWeight: 500,
          markers: {
            width: 12,
            height: 12,
            radius: 6
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val.toFixed(1) + "%";
          },
          style: {
            fontSize: '12px',
            fontWeight: 'bold',
            colors: ['#fff']
          }
        }
      },

      enhancedChartOptions3: {
        chart: {
          type: 'bar',
          height: 400,
          fontFamily: 'Roboto, sans-serif',
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false
            }
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800,
          }
        },
        colors: ['#667eea'],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded',
            borderRadius: 8,
          },
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val + ' คน';
          },
          style: {
            fontSize: '12px',
            fontWeight: 'bold',
            colors: ['#fff']
          }
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent']
        },
        xaxis: {
          categories: [],
          labels: {
            style: {
              fontSize: '12px',
              fontWeight: 500
            }
          }
        },
        yaxis: {
          title: {
            text: 'จำนวน (คน)',
            style: {
              fontSize: '14px',
              fontWeight: 600
            }
          },
          labels: {
            formatter: function(val) {
              return val + ' คน';
            }
          }
        },
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'light',
            type: 'vertical',
            shadeIntensity: 0.25,
            gradientToColors: ['#764ba2'],
            inverseColors: false,
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 100]
          }
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val + " คน"
            }
          }
        },
        grid: {
          borderColor: '#f1f1f1',
          strokeDashArray: 5,
        }
      }
    };
  },

  async mounted() {
    this.loading = true;
    try {
      await Promise.all([
        this.getAlldata(),
        this.getAlldata_rang(),
        this.getAlldata_retire()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      this.loading = false;
    }
  },

  methods: {
    formatDate(date) {
      return new Intl.DateTimeFormat('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    },

    getGradientColor(index) {
      const gradients = [
        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
        'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'
      ];
      return gradients[index % gradients.length];
    },

    getIcon(index) {
      const icons = [
        'mdi-account-tie',
        'mdi-school',
        'mdi-account-supervisor',
        'mdi-account-group',
        'mdi-briefcase',
        'mdi-account-star'
      ];
      return icons[index % icons.length];
    },

    getProgressColor(index) {
      const colors = ['primary', 'secondary', 'accent', 'success', 'info', 'warning'];
      return colors[index % colors.length];
    },

    getPercentage(count) {
      const total = this.showAlldata.reduce((sum, item) => sum + Number(item.countPerson), 0);
      return total > 0 ? Math.round((count / total) * 100) : 0;
    },

    async getAlldata() {
      try {
        let result = await this.$http.post("show_dashboard_all.php", {
          showCountAll: "ok"
        });
        this.showAlldata = result.data;
        
        let valuesst = [];
        let labelText = [];
        let data = this.showAlldata;
        
        data.forEach(value => {
          labelText.push(value.position_name + " (" + value.countPerson + " คน)");
          valuesst.push(
            parseFloat((value.countPerson * 100) / value.countAll).toFixed(2)
          );
        });

        this.series = valuesst.map(i => Number(i));
        this.enhancedChartOptions = {
          ...this.enhancedChartOptions,
          labels: labelText
        };
      } catch (error) {
        console.error('Error fetching all data:', error);
      }
    },

    async getAlldata_rang() {
      try {
        let result = await this.$http.post("show_dashboard_all.php", {
          showCountTeacher: "Ok"
        });
        this.showAlldata_rang = result.data;
        
        let valuesst = [];
        let labelText = [];
        let data = this.showAlldata_rang;
        
        data.forEach(value => {
          labelText.push(value.rang_name + " (" + value.countTeach + " คน)");
          valuesst.push(
            parseFloat((value.countTeach * 100) / value.countAll).toFixed(2)
          );
        });

        this.series2 = valuesst.map(i => Number(i));
        this.enhancedChartOptions2 = {
          ...this.enhancedChartOptions2,
          labels: labelText
        };
      } catch (error) {
        console.error('Error fetching teacher data:', error);
      }
    },

    async getAlldata_retire() {
      try {
        let result = await this.$http.post("show_dashboard_all.php", {
          showRetire: "Ok"
        });
        this.showAlldata_retire = result.data;
        
        let labelText = [];
        let valuesst = [];
        let data = this.showAlldata_retire;
        
        data.forEach(value => {
          labelText.push(value.position_name);
          valuesst.push(value.countRetrire);
        });

        this.series3 = [{
          name: "จำนวนผู้เกษียณ",
          data: valuesst.map(i => Number(i))
        }];
        
        this.enhancedChartOptions3 = {
          ...this.enhancedChartOptions3,
          xaxis: {
            ...this.enhancedChartOptions3.xaxis,
            categories: labelText
          }
        };
      } catch (error) {
        console.error('Error fetching retirement data:', error);
      }
    }
  }
};
</script>

<style scoped>
.dashboard-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 24px 16px;
}

/* Header Styles */
.dashboard-header {
  animation: fadeInDown 0.8s ease-out;
}

.header-content {
  text-align: left;
}

.dashboard-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.dashboard-subtitle {
  font-size: 1.1rem;
  color: #7f8c8d;
  margin: 0;
  font-weight: 500;
}

.header-stats {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
}

/* Stats Cards */
.stats-section {
  animation: fadeInUp 0.8s ease-out 0.2s both;
}

.stats-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  animation: slideInUp 0.8s ease-out both;
  height: 100%;
}

.stats-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.stats-card-content {
  padding: 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.stats-icon-wrapper {
  position: relative;
  margin-bottom: 20px;
}

.stats-icon-bg {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
}

.stats-icon-bg::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  animation: shimmer 3s linear infinite;
}

.stats-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  background: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.stats-content {
  flex: 1;
  margin-bottom: 16px;
}

.stats-value {
  font-size: 2.5rem;
  font-weight: 800;
  color: #2c3e50;
  line-height: 1;
  margin-bottom: 8px;
}

.stats-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #34495e;
  margin-bottom: 4px;
  line-height: 1.3;
}

.stats-subtitle {
  font-size: 0.9rem;
  color: #7f8c8d;
  display: flex;
  align-items: center;
  gap: 4px;
}

.stats-progress {
  display: flex;
  align-items: center;
  gap: 12px;
}

.progress-text {
  font-size: 0.85rem;
  font-weight: 600;
  color: #7f8c8d;
  min-width: 40px;
}

/* Charts Section */
.charts-section {
  animation: fadeInUp 0.8s ease-out 0.4s both;
}

.chart-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
  height: 100%;
}

.chart-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.1);
}

.chart-header {
  padding: 24px 24px 12px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.chart-title-content {
  display: flex;
  align-items: center;
  flex: 1;
}

.chart-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
}

.chart-subtitle {
  font-size: 0.95rem;
  color: #7f8c8d;
  margin: 4px 0 0 0;
  font-weight: 500;
}

.chart-content {
  padding: 16px 24px 24px 24px;
}

.chart-wrapper {
  position: relative;
  overflow: hidden;
  border-radius: 12px;
}

/* Animations */
.animate-card {
  animation: slideInUp 0.8s ease-out both;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
  }
}

/* Loading Overlay */
/* .loading-overlay {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
} */

.loading-text {
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
  text-align: center;
}

/* Responsive Design */
@media (max-width: 960px) {
  .dashboard-title {
    font-size: 2rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .header-stats {
    justify-content: flex-start;
    margin-top: 16px;
  }
  
  .stats-card-content {
    padding: 20px;
  }
  
  .stats-value {
    font-size: 2rem;
  }
  
  .chart-header {
    padding: 20px 20px 12px 20px;
  }
  
  .chart-content {
    padding: 12px 20px 20px 20px;
  }
}

@media (max-width: 600px) {
  .dashboard-container {
    padding: 16px 8px;
  }
  
  .dashboard-title {
    font-size: 1.8rem;
  }
  
  .dashboard-subtitle {
    font-size: 1rem;
  }
  
  .stats-card-content {
    padding: 16px;
  }
  
  .stats-icon-bg {
    width: 56px;
    height: 56px;
  }
  
  .stats-value {
    font-size: 1.8rem;
  }
  
  .chart-title {
    font-size: 1.2rem;
  }
  
  .chart-subtitle {
    font-size: 0.9rem;
  }
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}
</style>